import type { ConnectionCatalogItem, ToolCallRef } from "@rakazo/contracts";

/**
 * Tool calls the runtime describes with its own live line. Clients name each one in their
 * language; `detail` is the argument that line shows (a path, query, url or name).
 */
export const BUILTIN_ACTIVITY_TOOLS = [
  "shell",
  "user_computer_shell",
  "read_file",
  "write_file",
  "list_files",
  "attach_file",
  "open_path",
  "render_plot",
  "add_mcp_server",
  "computer_observe",
  "browser_navigate",
  "browser_snapshot",
  "browser_act",
  "computer_act",
  "run_subagent",
  "create_space",
  "list_fleet",
  "create_group",
  "update_group",
  "organize_bot",
  "set_child_instructions",
  "set_child_bot_model",
  "create_child_routine",
  "remember",
  "save_shared_memory",
  "web_search",
  "web_fetch",
  "skill_read",
  "skill_create",
  "skill_update",
  "skill_delete",
] as const;
export type BuiltinActivityTool = (typeof BUILTIN_ACTIVITY_TOOLS)[number];

const BUILTIN_TOOLS = new Set<string>(BUILTIN_ACTIVITY_TOOLS);

export function isBuiltinActivityTool(name: string): name is BuiltinActivityTool {
  return BUILTIN_TOOLS.has(name);
}

export type ComposioMetaTool =
  | "search_tools"
  | "multi_execute"
  | "manage_connections"
  | "remote_workbench"
  | "remote_bash"
  | "get_tool_schemas";

const COMPOSIO_META_TOOLS = new Map<string, ComposioMetaTool>([
  ["COMPOSIO_SEARCH_TOOLS", "search_tools"],
  ["COMPOSIO_MULTI_EXECUTE_TOOL", "multi_execute"],
  ["COMPOSIO_MANAGE_CONNECTIONS", "manage_connections"],
  ["COMPOSIO_REMOTE_WORKBENCH", "remote_workbench"],
  ["COMPOSIO_REMOTE_BASH_TOOL", "remote_bash"],
  ["COMPOSIO_GET_TOOL_SCHEMAS", "get_tool_schemas"],
]);

export type ToolApp = { slug: string; name: string; logo: string | null };

export type ToolVerb =
  | "fetch"
  | "list"
  | "search"
  | "send"
  | "reply"
  | "create"
  | "update"
  | "delete"
  | "move"
  | "upload"
  | "download"
  | "archive";

export type ToolObject =
  | "email"
  | "message"
  | "thread"
  | "draft"
  | "label"
  | "attachment"
  | "contact"
  | "event"
  | "calendar"
  | "file"
  | "folder"
  | "document"
  | "sheet"
  | "row"
  | "page"
  | "database"
  | "task"
  | "issue"
  | "pull_request"
  | "repository"
  | "channel"
  | "user"
  | "comment"
  | "meeting";

/** What a tool call does, for clients to word in their own language. Never holds display copy. */
export type ToolDisplay =
  | { kind: "builtin"; tool: BuiltinActivityTool; detail?: string }
  | { kind: "composio"; meta: ComposioMetaTool }
  /** `more` counts the batch's other actions. */
  | { kind: "app_action"; app: ToolApp; verb: ToolVerb; object: ToolObject; more: number }
  | { kind: "app"; app: ToolApp; more: number }
  | { kind: "mcp"; server: string; verb?: ToolVerb; object?: ToolObject; tool: string }
  | { kind: "unknown"; name: string };

const VERBS = new Map<string, ToolVerb>([
  ["FETCH", "fetch"],
  ["GET", "fetch"],
  ["READ", "fetch"],
  ["LIST", "list"],
  ["SEARCH", "search"],
  ["FIND", "search"],
  ["QUERY", "search"],
  ["SEND", "send"],
  ["REPLY", "reply"],
  ["CREATE", "create"],
  ["ADD", "create"],
  ["INSERT", "create"],
  ["UPDATE", "update"],
  ["EDIT", "update"],
  ["MODIFY", "update"],
  ["PATCH", "update"],
  ["DELETE", "delete"],
  ["REMOVE", "delete"],
  ["TRASH", "delete"],
  ["MOVE", "move"],
  ["UPLOAD", "upload"],
  ["DOWNLOAD", "download"],
  ["ARCHIVE", "archive"],
]);

const OBJECTS = new Map<string, ToolObject>([
  ["EMAIL", "email"],
  ["MESSAGE", "message"],
  ["THREAD", "thread"],
  ["DRAFT", "draft"],
  ["LABEL", "label"],
  ["ATTACHMENT", "attachment"],
  ["CONTACT", "contact"],
  ["EVENT", "event"],
  ["CALENDAR", "calendar"],
  ["FILE", "file"],
  ["FOLDER", "folder"],
  ["DOC", "document"],
  ["DOCUMENT", "document"],
  ["SHEET", "sheet"],
  ["SPREADSHEET", "sheet"],
  ["ROW", "row"],
  ["PAGE", "page"],
  ["DATABASE", "database"],
  ["TASK", "task"],
  ["ISSUE", "issue"],
  ["PULL_REQUEST", "pull_request"],
  ["REPO", "repository"],
  ["REPOSITORY", "repository"],
  ["CHANNEL", "channel"],
  ["USER", "user"],
  ["COMMENT", "comment"],
  ["MEETING", "meeting"],
]);

/** Words a slug puts around its object without changing it. */
const FILLER = new Set(["A", "AN", "THE", "ALL", "NEW", "MY", "SINGLE"]);
const PREPOSITIONS = new Set(["TO", "FROM", "IN", "INTO", "ON", "BY", "FOR", "WITH", "OF", "AT"]);

const NAME_MAX = 200;
const MAX_ACTIONS = 5;
const ACTION_SLUG = /^[A-Za-z0-9][A-Za-z0-9_-]{0,199}$/;

/**
 * A ref within the contract's limits, or undefined when `name` cannot be one. Batch actions keep
 * only slug-shaped names, once each.
 */
export function toolCallRef(
  name: string,
  options: { actions?: readonly string[]; detail?: string } = {},
): ToolCallRef | undefined {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length > NAME_MAX) return undefined;
  const actions = [...new Set((options.actions ?? []).map((action) => action.trim()))]
    .filter((action) => ACTION_SLUG.test(action))
    .slice(0, MAX_ACTIONS);
  const detail = options.detail?.trim();
  return {
    name: trimmed,
    ...(actions.length > 0 ? { actions } : {}),
    ...(detail
      ? { detail: detail.length > NAME_MAX ? `${detail.slice(0, NAME_MAX - 1)}…` : detail }
      : {}),
  };
}

/** Apps a catalog can name tool slugs after, one per slug. */
export function toolAppsFromCatalog(
  catalog: readonly Pick<ConnectionCatalogItem, "slug" | "name" | "logo">[],
): ToolApp[] {
  const bySlug = new Map<string, ToolApp>();
  for (const item of catalog) {
    const key = item.slug.trim().toLowerCase();
    if (!key || !item.name.trim()) continue;
    const known = bySlug.get(key);
    if (!known || (!known.logo && item.logo)) {
      bySlug.set(key, { slug: item.slug.trim(), name: item.name.trim(), logo: item.logo });
    }
  }
  return [...bySlug.values()];
}

/**
 * Names a tool call: a builtin with its detail, a Composio meta tool, an app action from the
 * slug (a batch call names its first inner action), an MCP tool, or the bare name.
 */
export function describeToolCall(
  ref: Pick<ToolCallRef, "name" | "actions" | "detail">,
  apps: readonly ToolApp[],
): ToolDisplay {
  const name = ref.name.trim();
  const actions = ref.actions?.filter((action) => action.trim()) ?? [];
  const first = actions[0] ? describeAppTool(actions[0], apps) : undefined;
  if (first) return { ...first, more: actions.length - 1 };
  if (isBuiltinActivityTool(name)) {
    const detail = ref.detail?.trim();
    return detail ? { kind: "builtin", tool: name, detail } : { kind: "builtin", tool: name };
  }
  const meta = COMPOSIO_META_TOOLS.get(name.toUpperCase());
  if (meta) return { kind: "composio", meta };
  const mcp = name.match(/^mcp__(.+?)__(.+)$/);
  if (mcp) {
    const [, server = "", tool = ""] = mcp;
    return { kind: "mcp", server, tool, ...parseAction(words(tool), "") };
  }
  return describeAppTool(name, apps) ?? { kind: "unknown", name };
}

/** The app a display names, for its icon. */
export function toolDisplayApp(display: ToolDisplay): ToolApp | undefined {
  return display.kind === "app" || display.kind === "app_action" ? display.app : undefined;
}

/**
 * Reverse of humanizeToolName for steps stored before `tool` existed. The case is lost, so an
 * app tool only names its app when the catalog knows the slug.
 */
export function toolRefFromStepLabel(label: string): ToolCallRef | undefined {
  const name = label.trim().toLowerCase().replaceAll(" ", "_");
  return /^[a-z0-9_-]+$/.test(name) ? { name } : undefined;
}

function describeAppTool(
  name: string,
  apps: readonly ToolApp[],
): Extract<ToolDisplay, { kind: "app" | "app_action" }> | undefined {
  const lower = name.toLowerCase();
  let app: ToolApp | undefined;
  for (const candidate of apps) {
    const slug = candidate.slug.toLowerCase();
    if (slug && lower.startsWith(`${slug}_`) && slug.length > (app?.slug.length ?? 0)) {
      app = candidate;
    }
  }
  let rest = app ? name.slice(app.slug.length + 1) : "";
  if (!app) {
    // Composio app slugs are upper case and start with their toolkit.
    const slug = name.match(/^([A-Z][A-Z0-9]*)_([A-Z0-9_]+)$/);
    if (!slug?.[1] || !slug[2]) return undefined;
    app = {
      slug: slug[1].toLowerCase(),
      name: slug[1].charAt(0) + slug[1].slice(1).toLowerCase(),
      logo: null,
    };
    rest = slug[2];
  }
  const action = parseAction(words(rest), app.slug.toUpperCase());
  return action ? { kind: "app_action", app, ...action, more: 0 } : { kind: "app", app, more: 0 };
}

/** Upper-case words of a slug or camelCase tool name. */
function words(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((word) => word.toUpperCase());
}

/**
 * The first dictionary verb and the object it acts on: the head of the noun run after it
 * (GMAIL_CREATE_EMAIL_DRAFT is a draft), or the noun just before it (EVENTS_LIST). Both must be
 * in the dictionaries.
 */
function parseAction(
  tokens: readonly string[],
  appToken: string,
): { verb: ToolVerb; object: ToolObject } | undefined {
  const at = tokens.findIndex((token) => lookup(VERBS, token));
  const verb = at >= 0 ? lookup(VERBS, tokens[at] ?? "") : undefined;
  if (!verb) return undefined;
  const object = objectAfter(tokens, at + 1, appToken) ?? objectBefore(tokens, at, appToken);
  return object ? { verb, object } : undefined;
}

function objectAfter(
  tokens: readonly string[],
  start: number,
  appToken: string,
): ToolObject | undefined {
  let object: ToolObject | undefined;
  // An unknown word before any noun is a modifier (AUTHENTICATED_USER) or the object itself
  // (COMMITS_ON_A_PULL_REQUEST); a preposition after it means the latter.
  let modified = false;
  for (let index = start; index < tokens.length; index += 1) {
    const noun = nounAt(tokens, index);
    if (noun) {
      object = noun.object;
      index += noun.width - 1;
      continue;
    }
    if (object) break;
    const token = tokens[index] ?? "";
    if (FILLER.has(token) || token === appToken) continue;
    if (PREPOSITIONS.has(token)) {
      if (modified) return undefined;
      continue;
    }
    modified = true;
  }
  return object;
}

function objectBefore(
  tokens: readonly string[],
  end: number,
  appToken: string,
): ToolObject | undefined {
  for (let index = end - 1; index >= 0; index -= 1) {
    const token = tokens[index] ?? "";
    if (FILLER.has(token) || token === appToken) continue;
    const pair = index > 0 ? lookup(OBJECTS, `${tokens[index - 1]}_${token}`) : undefined;
    return pair ?? lookup(OBJECTS, token);
  }
  return undefined;
}

function nounAt(
  tokens: readonly string[],
  index: number,
): { object: ToolObject; width: number } | undefined {
  const next = tokens[index + 1];
  const pair = next ? lookup(OBJECTS, `${tokens[index]}_${next}`) : undefined;
  if (pair) return { object: pair, width: 2 };
  const single = lookup(OBJECTS, tokens[index] ?? "");
  return single ? { object: single, width: 1 } : undefined;
}

/** A dictionary word, also in its plural or third-person form (EMAILS, REPOSITORIES, SENDS). */
function lookup<T>(dictionary: ReadonlyMap<string, T>, word: string): T | undefined {
  return (
    dictionary.get(word) ??
    (word.endsWith("S") ? dictionary.get(word.slice(0, -1)) : undefined) ??
    (word.endsWith("ES") ? dictionary.get(word.slice(0, -2)) : undefined) ??
    (word.endsWith("IES") ? dictionary.get(`${word.slice(0, -3)}Y`) : undefined)
  );
}
